import type { StoryPlaybackResponse } from '@manasil/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { api, resolveMediaUrl } from './api.js';

/**
 * One audio element for the whole portal, so starting day 2 stops day 1 rather than
 * playing both. The link is fetched fresh on each play — it is short-lived by design.
 */
interface NowPlaying {
  storyId: string;
  title: string;
  /** What the editor shows above the title, e.g. "Day 3". */
  context: string;
}

interface PlayerValue {
  now: NowPlaying | null;
  playing: boolean;
  loading: boolean;
  error: string | null;
  positionSec: number;
  durationSec: number;
  play: (story: NowPlaying) => Promise<void>;
  toggle: () => void;
  seek: (seconds: number) => void;
  skip: (delta: number) => void;
  close: () => void;
}

const PlayerContext = createContext<PlayerValue | null>(null);

export function PlayerProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [now, setNow] = useState<NowPlaying | null>(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [positionSec, setPositionSec] = useState(0);
  const [durationSec, setDurationSec] = useState(0);

  // Created once, outside React's tree — an <audio> in JSX would remount on every
  // re-render of whatever screen happened to hold it.
  if (audioRef.current === null && typeof Audio !== 'undefined') {
    audioRef.current = new Audio();
  }

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => setPositionSec(audio.currentTime);
    const onMeta = () => {
      if (Number.isFinite(audio.duration)) setDurationSec(audio.duration);
    };
    const onEnd = () => {
      setPlaying(false);
      setPositionSec(0);
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onError = () => {
      setError('That audio could not be played. The link may have expired — try again.');
      setPlaying(false);
    };

    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('ended', onEnd);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('ended', onEnd);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('error', onError);
      audio.pause();
    };
  }, []);

  const play = useCallback(
    async (story: NowPlaying) => {
      const audio = audioRef.current;
      if (!audio) return;

      // Same story: treat a second click as pause/resume rather than a restart.
      if (now?.storyId === story.storyId && audio.src) {
        if (audio.paused) void audio.play();
        else audio.pause();
        return;
      }

      setLoading(true);
      setError(null);
      setNow(story);
      setPositionSec(0);
      setDurationSec(0);

      try {
        const link = await api<StoryPlaybackResponse>(`/admin/stories/${story.storyId}/playback`);
        audio.src = resolveMediaUrl(link.url);
        setDurationSec(link.durationSec);
        await audio.play();
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'Could not play that audio');
        setPlaying(false);
      } finally {
        setLoading(false);
      }
    },
    [now?.storyId],
  );

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio?.src) return;
    if (audio.paused) void audio.play();
    else audio.pause();
  }, []);

  const seek = useCallback((seconds: number) => {
    const audio = audioRef.current;
    if (!audio?.src) return;
    audio.currentTime = seconds;
    setPositionSec(seconds);
  }, []);

  const skip = useCallback((delta: number) => {
    const audio = audioRef.current;
    if (!audio?.src) return;
    const next = Math.min(Math.max(0, audio.currentTime + delta), audio.duration || 0);
    audio.currentTime = next;
    setPositionSec(next);
  }, []);

  const close = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
    setNow(null);
    setPlaying(false);
    setPositionSec(0);
    setDurationSec(0);
    setError(null);
  }, []);

  const value = useMemo(
    () => ({
      now,
      playing,
      loading,
      error,
      positionSec,
      durationSec,
      play,
      toggle,
      seek,
      skip,
      close,
    }),
    [now, playing, loading, error, positionSec, durationSec, play, toggle, seek, skip, close],
  );

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

export function usePlayer(): PlayerValue {
  const value = useContext(PlayerContext);
  if (!value) throw new Error('usePlayer must be used inside PlayerProvider');
  return value;
}
